import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AccessibilityProvider } from "@/contexts/AccessibilityContext";
import { AccessibilityWidget } from "@/components/AccessibilityWidget";
import { FloatingSupportButton } from "@/components/FloatingSupportButton";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ActivityTrackingProvider } from "@/components/ActivityTrackingProvider";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ClientLogin from "./pages/ClientLogin";
import ResetPassword from "./pages/ResetPassword";
import WhyTPS from "./pages/WhyTPS";
import Services from "./pages/Services";
import Testimonials from "./pages/Testimonials";
import Reports from "./pages/Reports";
import Messages from "./pages/Messages";
import Claims from "./pages/Claims";
import Dashboard from "./pages/Dashboard";
import ClaimDetail from "./pages/ClaimDetail";
import ClaimAttachments from "./pages/ClaimAttachments";
import Profile from "./pages/Profile";
import AdminPanel from "./pages/AdminPanel";
import Analytics from "./pages/Analytics";
import Notifications from "./pages/Notifications";

import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import UserManagement from "./pages/UserManagement";
import LandlordDashboard from "./pages/LandlordDashboard";
import AdjusterDashboard from "./pages/AdjusterDashboard";
import ClientDashboard from "./pages/ClientDashboard";
import PropertySearch from "./pages/PropertySearch";
import ClientMatching from "./pages/ClientMatching";
import PlacementAnalytics from "./pages/PlacementAnalytics";
import { AdminLayout } from "./components/AdminLayout";
import AdminDashboard from "./pages/AdminDashboard";
import AdminClaimsManagement from "./pages/AdminClaimsManagement";
import AdminWpPropertySubmissions from "./pages/AdminWpPropertySubmissions";
import AdminWpClaimSubmissions from "./pages/AdminWpClaimSubmissions";
import AdminBroadcastNotifications from "./pages/AdminBroadcastNotifications";
import AdminNotificationAnalytics from "./pages/AdminNotificationAnalytics";
import AdminLogsDashboard from "./pages/AdminLogsDashboard";
import AdminLogRetentionSettings from "./pages/AdminLogRetentionSettings";
import AdminMessages from "./pages/AdminMessages";
import AdminProperties from "./pages/AdminProperties";
import SuperuserSettings from "./pages/SuperuserSettings";

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AccessibilityProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ImpersonationProvider>
            <ActivityTrackingProvider>
            <ImpersonationBanner />
            <AccessibilityWidget />
            <FloatingSupportButton />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/client-login" element={<ClientLogin />} />
              <Route path="/client-login/" element={<ClientLogin />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/why-tps" element={<WhyTPS />} />
              <Route path="/services" element={<Services />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/claims" element={<Claims />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/claim/:id" element={<ClaimDetail />} />
              <Route path="/claim/:id/attachments" element={<ClaimAttachments />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/notifications" element={<Notifications />} />

              {/* Admin Portal Routes */}
              <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
              <Route path="/admin/users" element={<AdminLayout><UserManagement /></AdminLayout>} />
              <Route path="/admin/properties" element={<AdminLayout><AdminProperties /></AdminLayout>} />
              <Route path="/admin/analytics" element={<AdminLayout><Analytics /></AdminLayout>} />
              <Route path="/admin/claims" element={<AdminLayout><AdminClaimsManagement /></AdminLayout>} />
              <Route path="/admin/wp/properties" element={<AdminLayout><AdminWpPropertySubmissions /></AdminLayout>} />
              <Route path="/admin/wp/claims" element={<AdminLayout><AdminWpClaimSubmissions /></AdminLayout>} />
              <Route path="/admin/broadcasts" element={<AdminLayout><AdminBroadcastNotifications /></AdminLayout>} />
              <Route path="/admin/notification-analytics" element={<AdminLayout><AdminNotificationAnalytics /></AdminLayout>} />
              <Route path="/admin/logs" element={<AdminLayout><AdminLogsDashboard /></AdminLayout>} />
              <Route path="/admin/log-retention" element={<AdminLayout><AdminLogRetentionSettings /></AdminLayout>} />
              <Route path="/admin/reports" element={<AdminLayout><Reports /></AdminLayout>} />
              <Route path="/admin/settings" element={<AdminLayout><SuperuserSettings /></AdminLayout>} />
              <Route path="/admin/messages" element={<AdminLayout><AdminMessages /></AdminLayout>} />

              <Route path="/messages" element={<Messages />} />

              <Route path="/old-admin" element={<AdminPanel />} />
              <Route path="/user-management" element={<UserManagement />} />
              <Route path="/landlord-dashboard" element={<LandlordDashboard />} />
              <Route path="/adjuster-dashboard" element={<AdjusterDashboard />} />
              <Route path="/client-dashboard" element={<ClientDashboard />} />
              <Route path="/property-search" element={<PropertySearch />} />
              <Route path="/client-matching" element={<ClientMatching />} />
              <Route path="/placement-analytics" element={<PlacementAnalytics />} />
              <Route path="/analytics" element={<Analytics />} />

              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </ActivityTrackingProvider>
            </ImpersonationProvider>
          </BrowserRouter>
        </AccessibilityProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;